import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Mail, Trash2, Check, MessageSquare } from "lucide-react";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesManager() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact/submissions`, getAuthHeaders());
      setMessages(response.data);
    } catch (err) {
      logger.error("Error fetching messages:", err);
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact/submissions/${messageId}/read`, {}, getAuthHeaders());
      fetchMessages();
    } catch (err) {
      toast.error("Failed to mark as read");
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;

    try {
      await axios.delete(`${API}/contact/submissions/${messageId}`, getAuthHeaders());
      toast.success("Message deleted!");
      fetchMessages();
    } catch (err) {
      toast.error("Failed to delete message");
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  if (loading) {
    return <div className="text-gold animate-pulse">Loading messages...</div>;
  }

  return (
    <div data-testid="messages-manager">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-3xl text-white">Messages</h1>
          {unreadCount > 0 && (
            <span className="px-3 py-1 bg-casino-red text-white text-sm rounded-full">
              {unreadCount} unread
            </span>
          )}
        </div>
        <p className="text-white/60 mt-2">Contact form submissions from visitors</p>
      </div>

      {messages.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <MessageSquare className="w-12 h-12 text-gold/30 mx-auto mb-4" />
          <p className="text-white/60">No messages yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`glass-card p-6 ${!message.is_read ? 'border-gold/30' : ''}`}
              data-testid={`message-${message.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Mail className={`w-5 h-5 ${message.is_read ? 'text-white/30' : 'text-gold'}`} />
                    <span className="text-white font-medium">{message.first_name ? `${message.first_name} ${message.last_name || ''}` : message.name}</span>
                    {!message.is_read && (
                      <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded">New</span>
                    )}
                  </div>
                  <p className="text-gold text-sm mb-1">{message.email}</p>
                  {message.phone && (
                    <p className="text-white/50 text-sm mb-2">Phone: {message.phone} {message.sms_consent && <span className="text-green-400 text-xs ml-2">(SMS opted in)</span>}</p>
                  )}
                  <p className="text-white/70">{message.message}</p>
                  <p className="text-white/40 text-xs mt-3">
                    {new Date(message.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!message.is_read && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markAsRead(message.id)}
                      className="border-white/20 text-white hover:border-green-500 hover:text-green-500"
                      data-testid={`mark-read-${message.id}`}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(message.id)}
                    className="border-white/20 text-white hover:border-red-500 hover:text-red-500"
                    data-testid={`delete-message-${message.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
